/**
 * @file Commandering related utils.
 */

import uniqBy from 'lodash.uniqby';
import groupBy from 'lodash.groupby';

import { VOTE_DECISION } from '../../../constants/vote-decisions';
import { IDENTITY_SELFIE_TYPE } from '../../../constants/identity';
import {
  COMMAND_MATCH_FLUSH_THRESHOLD,
  DETECTED_RACE_TYPE,
} from '../constants/command';
import getConfig from '../../../util/config';
import { sentryCaptureMessage } from '../../../util/sentry';
import getLogger from '../../../util/logger';

const log = getLogger('CommandUtils');

/**
 * Returns whether a Face match vote is FNS or not.
 *
 * @param {FaceMatchVote} vote The Vote to test.
 * @returns {Boolean} Whether the vote is FNS or not.
 */
const isFNSVote = vote => {
  return vote.not_sure_flusher_count > 0;
};

/**
 * Filters an array of FaceMatchVotes and returns only
 * the POSSIBLE (WUT/"Maybes") ones.
 *
 * @param {Array<FaceMatchVote>} votes The array of votes.
 * @returns {Array<FaceMatchVote>} The maybe votes.
 */
const getMaybeVotes = (votes = []) => {
  return votes.filter(vote => {
    return vote.vote === VOTE_DECISION.WUT;
  });
};

/**
 * Filters an array of FaceMatchVotes and returns only
 * the POSITIVE (TOTALLY) ones.
 *
 * @param {Array<FaceMatchVote>} votes The array of votes.
 * @returns {Array<FaceMatchVote>} The positive votes.
 */
const getPositiveVotes = (votes = []) => {
  return votes.filter(vote => {
    return vote.vote === VOTE_DECISION.TOTALLY;
  });
};

/**
 * Filters an array of FaceMatchVotes and returns FNS or not FNS votes
 * depending on the isFNS param.
 *
 * @param {Array<FaceMatchVote>} votes The array of votes.
 * @param {boolean} isFNS Whether to get votes that are FNS or nto.
 * @returns {Array<FaceMatchVote>} The positive votes.
 */
const getVotesByFNS = (votes = [], isFNS = false) => {
  return getPositiveVotes(votes).filter(vote =>
    isFNS ? isFNSVote(vote) : !isFNSVote(vote),
  );
};

const getConfidenceHslColor = confidence =>
  `hsl(${confidence * 120}, 100%, 50%)`;

const formatConfidence = confidence => (confidence * 100).toFixed(1);

/**
 * Returns the minimum or maximum confidence score in a
 * array of votes.
 *
 * @param {array<FaceMatchVote>} votes The votes.
 * @returns {{min: number, max: number}}
 */
const getMinMaxConfidenceScores = votes => {
  const scores = votes.map(vote => vote.machine_confidence);

  return {
    min: Math.min(...scores),
    max: Math.max(...scores),
  };
};

const deduplicateVotes = votes => {
  if (!votes) {
    return [];
  }

  const unique = uniqBy(votes, ({ id }) => id);

  if (unique.length !== votes.length) {
    log.warn('deduplicateVotes :: Non-unique votes found. ', votes);
    sentryCaptureMessage('Non-unique votes found.', {
      level: 'error',
      extra: {
        votes,
      },
    });
  }

  return unique;
};

/**
 * Sorts votes by machine confidence (descending).
 *
 * @param {Array<Object>} votes The face_match_votes with machine_confidence keys.
 * @returns {Array<Object>} Sorted votes by machine confidence.
 */
const sortVotesByHighestConfidence = votes => {
  return [...votes].sort((a, b) => {
    return b.machine_confidence - a.machine_confidence;
  });
};

/**
 * Groups votes into clusters after sorting them by Highest Confidence Match.
 *
 * @param {Array<Object>} votes The face_match_votes with machine_confidence keys.
 * @returns {Array<Object>} Sorted votes by machine confidence.
 */
const getClusteredMaybes = (votes = []) => {
  const maybes = getMaybeVotes(votes);
  const orderedMaybes = sortVotesByHighestConfidence(maybes);
  const clusteredMaybes = groupBy(orderedMaybes, 'cluster_id');
  const clusters = Object.keys(clusteredMaybes).map(
    key => clusteredMaybes[key],
  );

  return clusters.sort(
    ([a], [b]) => b.machine_confidence - a.machine_confidence,
  );
};

/**
 * Returns all active reference identity selfies
 * sorted by most recent (based on created_at field).
 *
 * @param {Array<Object>} selfies The array of IdentitySelfie objects.
 * @param {Number} limit Limit of identity selfies to return.
 * @returns {Array} The active reference identity selfies.
 */
const getActiveReferenceIdentitySelfies = (selfies, limit = 5) => {
  if (!selfies) {
    return [];
  }

  const matchEnabledSelfies = selfies.filter(selfie => selfie.matching_enabled);

  const sorted = [...matchEnabledSelfies].sort((a, b) => {
    return new Date(b.created_at) - new Date(a.created_at);
  });

  return sorted.slice(0, limit);
};

/**
 * Returns all user provided identity selfies.
 *
 * @param {Array<IdentitySelfie>} selfies The array of IdentitySelfie objects.
 * @returns {Array} The user provided identity selfies.
 */
const getUserProvidedIdentitySelfies = (selfies = []) => {
  return selfies.filter(
    selfie => selfie.selfie_type === IDENTITY_SELFIE_TYPE.USER,
  );
};

/**
 * Returns all assigned identity selfies.
 *
 * @param {Array<IdentitySelfie>} selfies The array of IdentitySelfie objects.
 * @returns {Array} The assigned identity selfies.
 */
const getAssignedIdentitySelfies = (selfies = []) => {
  return selfies.filter(
    selfie => selfie.selfie_type === IDENTITY_SELFIE_TYPE.ASSIGNED,
  );
};

/**
 * Returns the avatar identity selfie.
 *
 * @param {Array<IdentitySelfie>} selfies The array of IdentitySelfie objects.
 * @returns {Array} The assigned identity selfies.
 */
const getAvatarIdentitySelfie = (selfies = []) => {
  return selfies.find(selfie => selfie.is_avatar);
};

/**
 * Returns the Waldo Admin link for an identity's account.
 *
 * @param {Object} identity The identity.
 * @returns {string} The Waldo Admin link.
 */
const getIdentityAccountAdminPath = identity => {
  const {
    app: { adminUrl },
  } = getConfig();

  const accountId = identity?.account?.id;

  return `${adminUrl}/accounts/${accountId}/`;
};

/**
 * Returns the album via a selected identity's album membership.
 *
 * @param {Object} selectedIdentity - The selected identity.
 * @returns {Object|undefined} - The album.
 */
const getSelectedIdentityAlbum = selectedIdentity => {
  return selectedIdentity?.album_membership?.album;
};

/**
 * Returns the Waldo Admin link for an identity's first
 * selected identity matching page.
 *
 * @param {Object} selectedIdentity The selected identity.
 * @param {Object} selectedIdentity.album_membership.album The album this selected identity belongs to.
 * @returns {string} The Waldo Admin link.
 */
const getSelectedIdentityAdminMatchingPath = selectedIdentity => {
  const {
    app: { adminUrl },
  } = getConfig();

  const album = getSelectedIdentityAlbum(selectedIdentity);
  const albumId = album?.id;

  return `${adminUrl}/matching/${selectedIdentity.id}/${albumId}/`;
};

/**
 * Checks whether a cluster of maybe votes should be
 * automatically flushed (i.e. Have their applied status `true`).
 *
 * @param {array<FaceMatchVote>} cluster The vote cluster.
 * @param {VOTE_DECISION} decision The decision for these votes.
 * @param {number} [threshold] The threshold.
 * @returns {boolean} `applied` status of the whole cluster.
 */
const getMaybeVotingAppliedStatus = (
  cluster,
  decision,
  threshold = COMMAND_MATCH_FLUSH_THRESHOLD,
) => {
  const { max } = getMinMaxConfidenceScores(cluster);

  return max >= threshold && decision === VOTE_DECISION.TOTALLY;
};

/**
 * Returns the Waldo Admin link for an album.
 *
 * @param {Object} album The selected identity.
 * @returns {string} The Waldo Admin link.
 */
const getAlbumAdminMatchingPath = album => {
  const {
    app: { adminUrl },
  } = getConfig();

  return `${adminUrl}/albums/${album.id}/`;
};

/**
 * Determines whether an album permits MatchFlushing.
 *
 * @param {Object} album - The album.
 * @returns {boolean}
 */
const canMatchFlush = album => {
  if (!album) {
    return false;
  }

  return album.commander_enabled_flush && !album.time_based_matching_enabled;
};

/**
 * Determines whether a vote is eligible for match flushing.
 *
 * @param {Object} vote - The vote.
 * @returns {boolean}
 */
const canMatchFlushVote = vote => {
  if (!vote) {
    return false;
  }

  const race = vote.compared_face.detected_race;

  return !race || race === DETECTED_RACE_TYPE.CAUCASIAN;
};

/**
 * Determines whether votes should be fetched for the match queue.
 *
 * @param {Array} queue - The match queue.
 * @param {boolean} loading - Whether we're already loading/fetching.
 * @param {boolean} done - Whether the queue is exhausted (all votes leased).
 * @returns {boolean}
 */
const shouldFetchMatchingVotes = (queue, loading, done) => {
  if (loading || done) {
    return false;
  }

  if (!queue.length) {
    return true;
  }

  const totalQueueVoteLength = queue.reduce(
    (count, batch) => batch.votes.length + count,
    0,
  );

  return totalQueueVoteLength <= 5;
};

const getManualMatchCastedVoteLabel = castedVote => {
  if (castedVote.vote === VOTE_DECISION.WUT) {
    return 'Vote Casted: NS';
  }

  return 'Vote Casted: Flushed';
};

export {
  getConfidenceHslColor,
  formatConfidence,
  getMinMaxConfidenceScores,
  deduplicateVotes,
  getClusteredMaybes,
  getMaybeVotes,
  getPositiveVotes,
  sortVotesByHighestConfidence,
  getActiveReferenceIdentitySelfies,
  getUserProvidedIdentitySelfies,
  getAssignedIdentitySelfies,
  getSelectedIdentityAdminMatchingPath,
  getIdentityAccountAdminPath,
  getAvatarIdentitySelfie,
  getMaybeVotingAppliedStatus,
  getSelectedIdentityAlbum,
  getAlbumAdminMatchingPath,
  canMatchFlush,
  shouldFetchMatchingVotes,
  getManualMatchCastedVoteLabel,
  canMatchFlushVote,
  getVotesByFNS,
  isFNSVote,
};
